import type { AutocompleteOption } from "@/components/Autocomplete/types";
import type { AutocompleteFetchFunction } from "@/components/form/types";
import { keepPreviousData, useQuery } from "@tanstack/react-query";

interface UseAutocompleteOptionsProps {
  queryKey: string[];
  fetchFn: AutocompleteFetchFunction;
  /** Pass the already-debounced search string — this hook does not debounce. */
  search: string;
  limit?: number;
  enabled?: boolean;
}

interface UseAutocompleteOptionsReturn {
  options: AutocompleteOption[];
  isFetching: boolean;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
}

/**
 * Fetches autocomplete options for a given search string.
 *
 * Debounce must be applied by the caller before passing `search`.
 * `keepPreviousData` keeps old results visible while the new fetch loads.
 */
export function useAutocompleteOptions({
  queryKey,
  fetchFn,
  search,
  limit = 20,
  enabled = true,
}: UseAutocompleteOptionsProps): UseAutocompleteOptionsReturn {
  const query = useQuery({
    // search and limit are part of the key — any change triggers a new fetch
    queryKey: [...queryKey, search, limit],
    queryFn: () => fetchFn({ search, limit }),
    enabled,
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
  });

  return {
    options: query.data ?? [],
    isFetching: query.isFetching,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  };
}
